class RealtimeDispatchSocketService {
  constructor() {
    this.socket = null;
    this.gatewayBase = "";
    this.token = "";
    this.connectionState = "connecting";
    this.authenticated = false;
    this.heartbeatTimer = null;
    this.reconnectTimer = null;
    this.joinedOrderIds = new Set();
    this.subscribers = new Map();
    this.nextSubscriberId = 1;
  }

  subscribe(options = {}) {
    const id = `dispatch-subscriber-${this.nextSubscriberId++}`;
    const subscriber = {
      onStateChange:
        typeof options.onStateChange === "function" ? options.onStateChange : null,
      onDispatchStatus:
        typeof options.onDispatchStatus === "function"
          ? options.onDispatchStatus
          : null,
      onEvent:
        typeof options.onEvent === "function" ? options.onEvent : null,
      orderIds: new Set(),
    };

    this.subscribers.set(id, subscriber);

    if (subscriber.onStateChange) {
      subscriber.onStateChange(this.connectionState);
    }

    return {
      updateOrderIds: (orderIds = []) => {
        const activeSubscriber = this.subscribers.get(id);
        if (!activeSubscriber) return;
        activeSubscriber.orderIds = this.normalizeOrderIds(orderIds);
        this.syncOrderSubscriptions();
      },
      dispose: () => {
        this.subscribers.delete(id);
        this.syncOrderSubscriptions();
        if (this.subscribers.size === 0) {
          this.disconnectSocket({ intentional: true });
        }
      },
    };
  }

  ensureConnection(options = {}) {
    const gatewayBase = String(options.gatewayBase || "").trim();
    const token = String(options.token || "").trim();

    if (!gatewayBase || !token) {
      this.setConnectionState("failed");
      this.disconnectSocket({ intentional: true });
      return;
    }

    const nextUrl = this.resolveWebSocketUrl(gatewayBase);
    if (!nextUrl) {
      this.setConnectionState("failed");
      this.disconnectSocket({ intentional: true });
      return;
    }

    const currentUrl = this.gatewayBase
      ? this.resolveWebSocketUrl(this.gatewayBase)
      : "";
    const shouldReconnect =
      this.socket == null || this.token !== token || currentUrl !== nextUrl;

    this.gatewayBase = gatewayBase;
    this.token = token;

    if (shouldReconnect) {
      this.connectSocket();
      return;
    }

    this.syncOrderSubscriptions();
  }

  normalizeOrderIds(orderIds) {
    const normalized = new Set();
    (Array.isArray(orderIds) ? orderIds : []).forEach((orderId) => {
      const value = String(orderId || "").trim();
      if (value) {
        normalized.add(value);
      }
    });
    return normalized;
  }

  resolveWebSocketUrl(gatewayBase) {
    try {
      const parsed = new URL(gatewayBase);
      parsed.protocol = parsed.protocol === "https:" ? "wss:" : "ws:";
      parsed.pathname = "/ws";
      parsed.search = "";
      parsed.hash = "";
      return parsed.toString();
    } catch (error) {
      return "";
    }
  }

  setConnectionState(state) {
    this.connectionState = state;
    this.subscribers.forEach((subscriber) => {
      if (subscriber.onStateChange) {
        subscriber.onStateChange(state);
      }
    });
  }

  emitDispatchStatus(payload) {
    this.subscribers.forEach((subscriber) => {
      if (subscriber.onDispatchStatus) {
        subscriber.onDispatchStatus(payload);
      }
    });
  }

  emitEvent(event) {
    this.subscribers.forEach((subscriber) => {
      if (subscriber.onEvent) {
        subscriber.onEvent(event);
      }
    });
  }

  getDesiredOrderIds() {
    const desiredOrderIds = new Set();
    this.subscribers.forEach((subscriber) => {
      subscriber.orderIds.forEach((orderId) => desiredOrderIds.add(orderId));
    });
    return desiredOrderIds;
  }

  syncOrderSubscriptions() {
    const desiredOrderIds = this.getDesiredOrderIds();

    if (
      !this.socket ||
      this.socket.readyState !== WebSocket.OPEN ||
      !this.authenticated
    ) {
      this.joinedOrderIds = desiredOrderIds;
      return;
    }

    desiredOrderIds.forEach((orderId) => {
      if (this.joinedOrderIds.has(orderId)) return;
      this.socket.send(
        JSON.stringify({
          type: "order.join",
          payload: { orderId },
        })
      );
    });

    this.joinedOrderIds.forEach((orderId) => {
      if (desiredOrderIds.has(orderId)) return;
      this.socket.send(
        JSON.stringify({
          type: "order.leave",
          payload: { orderId },
        })
      );
    });

    this.joinedOrderIds = desiredOrderIds;
  }

  sendEvent(event) {
    if (
      !event ||
      typeof event !== "object" ||
      !event.type ||
      !this.socket ||
      this.socket.readyState !== WebSocket.OPEN ||
      !this.authenticated
    ) {
      return false;
    }

    this.socket.send(
      JSON.stringify({
        type: event.type,
        payload:
          event.payload && typeof event.payload === "object" ? event.payload : {},
      })
    );
    return true;
  }

  startHeartbeat(socket) {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (
        socket &&
        socket === this.socket &&
        socket.readyState === WebSocket.OPEN
      ) {
        try {
          socket.send("ping");
        } catch (error) {
          // no-op
        }
      }
    }, 25000);
  }

  stopHeartbeat() {
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  scheduleReconnect() {
    clearTimeout(this.reconnectTimer);
    if (!this.subscribers.size || !this.gatewayBase || !this.token) {
      return;
    }
    this.reconnectTimer = setTimeout(() => {
      this.connectSocket();
    }, 3000);
  }

  connectSocket() {
    const websocketUrl = this.resolveWebSocketUrl(this.gatewayBase);
    if (!websocketUrl || !this.token) {
      this.setConnectionState("failed");
      return;
    }

    this.disconnectSocket({ intentional: true, preserveReconnectTimer: true });
    this.authenticated = false;
    this.joinedOrderIds = new Set();
    this.setConnectionState("connecting");

    const socket = new WebSocket(websocketUrl);
    this.socket = socket;

    socket.onopen = () => {
      this.setConnectionState("authenticating");
      this.startHeartbeat(socket);
      socket.send(
        JSON.stringify({
          type: "auth.login",
          payload: { token: this.token },
        })
      );
    };

    socket.onmessage = (event) => {
      if (event.data === "pong") {
        return;
      }

      let data = null;
      try {
        data = JSON.parse(event.data);
      } catch (error) {
        return;
      }

      if (!data || !data.type) return;
      if (data.type === "event.accepted") return;

      if (data.type === "auth.success") {
        this.authenticated = true;
        this.setConnectionState("connected");
        this.syncOrderSubscriptions();
        return;
      }

      if (data.type === "auth.error" || data.type === "order.error") {
        this.setConnectionState("failed");
        socket.__dispatchIntentionalClose = true;
        try {
          socket.close();
        } catch (error) {
          // no-op
        }
        this.scheduleReconnect();
        return;
      }

      if (
        data.type === "dispatch.status" ||
        data.type === "dispatch.status.update"
      ) {
        this.emitDispatchStatus(data.payload || {});
        return;
      }

      this.emitEvent(data);
    };

    socket.onerror = () => {
      this.setConnectionState("failed");
      this.scheduleReconnect();
    };

    socket.onclose = () => {
      this.stopHeartbeat();
      if (this.socket === socket) {
        this.socket = null;
      }
      this.authenticated = false;
      this.joinedOrderIds = new Set();
      if (socket.__dispatchIntentionalClose) {
        return;
      }
      this.setConnectionState("failed");
      this.scheduleReconnect();
    };
  }

  disconnectSocket(options = {}) {
    const intentional = options.intentional !== false;
    const preserveReconnectTimer = options.preserveReconnectTimer === true;

    if (!preserveReconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopHeartbeat();

    const socket = this.socket;
    this.socket = null;
    this.authenticated = false;
    this.joinedOrderIds = new Set();

    if (socket) {
      try {
        socket.__dispatchIntentionalClose = intentional;
        socket.close();
      } catch (error) {
        // no-op
      }
    }
  }
}

const realtimeDispatchSocket = new RealtimeDispatchSocketService();

export default realtimeDispatchSocket;
